'use client';

import type React from 'react';
import { BAND_BANNER_HEIGHT } from '@/constants/banner';
import { usePreviousElementStore } from '@/store/usePreviousElementStore';
import { MarketExhibitionGtm } from '@/utils/gtm/market-exhibition/MarketExhibitionGtm';
import { PromotionGtmElement } from '@/utils/gtm/promotion/promotion-gtm-element.enum';
import type { StripBannerItemRes } from '../api/banner/dto/response/banner.res.dto';
import PromotionBanner from '../app/promotion/components/PromotionBanner';
import { MAX_WIDTH_639PX, s3CloudFrontUrlEndpoint } from '../utils/common';
import { LOCAL_STORAGE_KEYS } from '../utils/common/local-storage-keys';
import useBandBanner from '../utils/hook/useBandBanner';
import useClientMediaQuery from '../utils/hook/useClientMediaQuery';
import UImage from './UImage';

const closeGrayIcon = `${s3CloudFrontUrlEndpoint}/svg/close-gray-icon.svg`;

interface ApiStripBannerProps {
  data: StripBannerItemRes;
}

/**
 * API 기반 띠배너 컴포넌트
 *
 * 어드민에서 등록한 마켓 전용 띠배너를 노출한다.
 * 닫기(다시 보지 않기), 모바일/PC 이미지 분기를 지원한다.
 */
const ApiStripBanner = ({ data }: ApiStripBannerProps) => {
  const isMobile = useClientMediaQuery({ query: MAX_WIDTH_639PX });
  const { handleNeverShowAgain } = useBandBanner();

  const setPreviousElement = usePreviousElementStore.use.setPreviousElement();

  const imageUrl = isMobile ? data.mobileImageUrl : data.pcImageUrl;
  const linkPath = data.linkUrl;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleNeverShowAgain(
      LOCAL_STORAGE_KEYS.BAND_BANNER.STRIP_BANNER_MARKER(data.id),
    );
  };

  const handleClick = () => {
    setPreviousElement(PromotionGtmElement.BAND_BANNER);
    new MarketExhibitionGtm().click_market_exhibition_link({
      exhibition_name: data.name,
      element: PromotionGtmElement.BAND_BANNER,
    });
  };

  if (!imageUrl) return null;

  const closeButtonClassName = isMobile
    ? 'absolute top-1/2 left-16px z-10 -translate-y-[50%]'
    : 'absolute right-20px top-1/2 z-10 -translate-y-[50%]';

  return (
    <div
      className="ApiStripBanner flex-center relative overflow-hidden leading-[0]"
      style={{ backgroundColor: data.backgroundColor }}
    >
      <button
        type="button"
        onClick={handleClose}
        className={closeButtonClassName}
        aria-label="띠배너 닫기"
      >
        <UImage src={closeGrayIcon} alt="닫기" width={24} height={24} />
      </button>
      {/* 표시 높이는 CSS로 고정 — preflight(img max-width:100%; height:auto)로 창 폭 따라 높이가 유동하는 것을 방지 */}
      <PromotionBanner
        href={linkPath ?? '#'}
        className="h-[72px] rounded-none object-cover sm:h-[56px]"
        src={imageUrl}
        alt="market-banner"
        onClick={handleClick}
        width={isMobile ? 375 : 1200}
        height={isMobile ? BAND_BANNER_HEIGHT.mobile : BAND_BANNER_HEIGHT.pc}
      />
    </div>
  );
};

export default ApiStripBanner;
